"use client";
import { useTranslations } from "next-intl";
import { Container, DataList, Flex, Skeleton } from "@radix-ui/themes";
import type { DataElement, DataInputCommonParams, DataInputParams, InputTypeData, RadarProps } from "./inputType";
import { Debug } from "@/util/debug";
import { GetDataInputElement } from "./inputFunction";
import { TransitionType } from "@/components/layout/layoutType";
import { SliceTooltipProps, PointTooltipProps, LineSeries, PointTooltipComponent } from "@nivo/line";
import DataInput, { GetDataLabel } from "../data/dataInput";
import { CSSProperties, useEffect, useMemo, useState } from "react";
import { getDataKeyLabel, getDataKeyList, getDataKeyName, getDataKeyValue } from "@/util/datautil";
import { getStringArr, getStringLabel, getStringRecord } from "@/util/arrayutil";
import { CartesianMarkerProps, DatumValue, PropertyAccessor } from "@nivo/core";
import { cn } from "@/lib/utils";
import dayjs from "dayjs";

import { ResponsiveLine } from "@nivo/line";
// import dynamic from "next/dynamic";
// const ResponsiveLine = dynamic(
//   () =>
//     import("@nivo/line")
//       .then((mod) => mod.ResponsiveLine)
//       .catch(() => {
//         const DummyComponent = () => <></>;
//         return DummyComponent;
//       }),
//   { ssr: false },
// );

type IndexInfo = {
  name: string | undefined;
  keys: string[] | undefined;
  keyLabel: Record<string, string> | undefined;
  fields: string[][] | undefined;
  fieldLabel: Record<string, string>[];
  indexBy: PropertyAccessor<Record<string, any>, string>;
  multi: boolean | undefined;
  convert: boolean;
};
const isTrainsitionFieldLabel = (params: DataInputParams, field: string) => {
  return (
    (params.radarProp?.fieldInputProp && params.radarProp?.fieldInputProp[field]
      ? params.radarProp?.fieldInputProp[field].labelTransition
      : undefined) ??
    params.radarProp?.commonInputProp?.labelTransition ??
    params.labelTransition ??
    true
  );
};
const chartCoverDivstyle: CSSProperties = {
  background: "var(--color-background)",
  color: "inherit",
  fontSize: "inherit",
  borderRadius: "2px",
  boxShadow: "rgba(0, 0, 0, 0.25) 0px 1px 2px",
  padding: "5px 9px",
};
const customSliceTooltip = (p: SliceTooltipProps<LineSeries>, checkPoint: any) => {
  //Debug(p);
  const temp_x = p.slice.points[0].data.x;
  if (!temp_x) return undefined;
  const check = checkPoint[new Date(temp_x).getTime()];
  //if (!check) return undefined;
  return (
    <div style={chartCoverDivstyle}>
      <div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td style={{ padding: "3px 5px", textAlign: "center" }} colSpan={3}>
                {(check ? check + " - " : "") + p.slice.points[0].data.xFormatted}
              </td>
            </tr>
            {p.slice.points.map((i) => {
              return (
                <tr key={"sliceTooltop_" + i}>
                  <td style={{ padding: "3px 5px" }}>
                    <span style={{ display: "block", width: "1rem", height: "1rem", background: i.color, marginRight: "0.5rem" }}></span>
                  </td>
                  <td style={{ padding: "3px 5px" }}>{i.seriesId}</td>
                  <td style={{ padding: "3px 5px" }}>
                    <span style={{ fontWeight: "bold" }}>{i.data.yFormatted}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
const customTooltip: PointTooltipComponent<LineSeries> = (a: PointTooltipProps<LineSeries>) => {
  const temp_x = a.point.data.x;
  if (!temp_x) return undefined;
  const time = new Date(temp_x).getTime();
  const str = time >= 0 ? dayjs(time).format("mm:ss") : "-" + dayjs(Math.abs(time)).format("mm:ss");
  return (
    <div style={chartCoverDivstyle}>
      <div style={{ whiteSpace: "pre", display: "flex", alignItems: "center" }}>
        <span style={{ display: "block", width: "1rem", height: "1rem", background: a.point.seriesColor, marginRight: "0.5rem" }}></span>
        <span style={{ color: a.point.seriesColor }}>
          <strong>{a.point.seriesId}</strong>
        </span>
        <span style={{ width: "1rem", textAlign: "center", color: a.point.seriesColor }}>:</span>
        <span>
          <strong>{str}</strong> - <strong>{a.point.data.yFormatted}</strong>
        </span>
      </div>
    </div>
  );
};
const getDefaultValue = (params: DataInputParams, field: string) => {
  return params.inputMode == "view"
    ? 0
    : params.radarProp?.fieldInputProp &&
        params.radarProp?.fieldInputProp[field] &&
        params.radarProp?.fieldInputProp[field].defaultValue !== undefined
      ? params.radarProp?.fieldInputProp[field].defaultValue
      : params.radarProp?.commonInputProp?.defaultValue !== undefined
        ? params.radarProp?.commonInputProp?.defaultValue
        : (params.defaultValue ?? 0);
};

export const dataConverter = (t: TransitionType, indexInfo: IndexInfo, params: DataInputParams): Record<string, unknown>[] => {
  //Debug(indexInfo);
  if (indexInfo.convert) {
    if (params.radarProp?.data) {
      //Debug("return data");
      return params.radarProp?.data;
    } else if (params.values === undefined) {
      //Debug("return values undefiend");
      return params.values;
    } else {
      if (indexInfo.fields) {
        const inputMode = params.inputMode === "view" && (params.noItem ?? true);
        const valueArr = inputMode ? params.values : params.form ? params.form.getFieldsValue() : {};
        const result = indexInfo.fields?.map((fields, index) => {
          const temp: Record<string, unknown> = {};
          fields.forEach((field) => {
            const indexBy = typeof indexInfo.indexBy == "string" ? indexInfo.indexBy : indexInfo.indexBy.toString();
            temp[indexBy] = isTrainsitionFieldLabel(params, field)
              ? t(indexInfo.fieldLabel[index][field])
              : indexInfo.fieldLabel[index][field];
            indexInfo.keys?.forEach((key, index) => {
              temp[indexInfo.keyLabel ? (indexInfo.keyLabel[key] ?? "") : ""] =
                (inputMode
                  ? valueArr[indexInfo.multi ? field + "_" + key : field]
                  : valueArr[indexInfo.multi ? field + "_" + key : field]) ?? getDefaultValue(params, field);
            });
          });
          return temp;
        });
        return result;
      } else {
        //Debug("return fields isnull");
        return [{}];
      }
    }
  } else {
    if (params.radarProp?.data) {
      return params.radarProp?.data;
    } else if (params.values instanceof Array) {
      return params.values;
    } else if (params.values === undefined) {
      return [{}];
    } else {
      return [params.values];
    }
  }
};
const data = [
  {
    color: "hsl(158, 70%, 50%)",
    data: [
      {
        x: new Date(-180000),
        y: 23,
      },
      {
        x: new Date(-90000),
        y: 190,
      },
      {
        x: new Date(0),
        y: 179.1,
      },
      {
        x: new Date(65000),
        y: 120.9,
      },
      {
        x: new Date(180000),
        y: 140,
      },
      {
        x: new Date(250000),
        y: 150,
      },
      {
        x: new Date(360000),
        y: 170,
      },
      {
        x: new Date(613000),
        y: 201.1,
      },
      {
        x: new Date(733000),
        y: 212,
      },
    ],
    id: "bean",
  },
  {
    data: [
      {
        x: new Date(-180000),
        y: 23,
      },
      {
        x: new Date(-90000),
        y: 210,
      },
      {
        x: new Date(0),
        y: 203,
      },
      {
        x: new Date(65000),
        y: 163,
      },
      {
        x: new Date(250000),
        y: 178,
      },
      {
        x: new Date(360000),
        y: 195.3,
      },
      {
        x: new Date(613000),
        y: 225,
      },
      {
        x: new Date(733000),
        y: 230,
      },
    ],
    id: "air",
  },
];

export function DataRadarItem(params: RadarProps) {
  //Debug(params.data);
  //const t:TicksSpec<any>;
  const lineStyle = {
    stroke: "var(--gray-11)",
    //strokeDasharray: "7,7",
    strokeWidth: 1,
    strokeOpacity: 0.2,
  };
  const checkPoint: Record<number, string> = { 0: "투입", 65000: "터닝", 250000: "옐로우", 613000: "1차", 733000: "배출" };
  checkPoint[-180000] = "예열";
  checkPoint[-90000] = "투입";
  const checkArr = Object.keys(checkPoint).map((key) => {
    return Number(key);
  });
  return (
    <ResponsiveLine
      data={data}
      axisBottom={{
        format: (a) => {
          return a >= 0 ? dayjs(a).format("mm:ss") : "-" + dayjs(Math.abs(a)).format("mm:ss");
        },
        tickValues: "every 2 minutes",
      }}
      axisTop={{
        format: (a) => {
          const prevStr = checkPoint[a] ?? undefined;
          return (
            (prevStr ? prevStr + " " : "") + (a > 0 ? dayjs(a).format("mm:ss") : a == 0 ? "" : "-" + dayjs(Math.abs(a)).format("mm:ss"))
          );
        },
        tickValues: checkArr,
      }}
      axisLeft={{
        legendOffset: 12,
        format: (a) => {
          return a + "°C";
        },
        tickValues: [0, 50, 100, 150, 200, 250],
      }}
      curve="monotoneX" //"step" | "linear" | "monotoneX" | "basis" | "cardinal" | "catmullRom" | "monotoneY" | "natural" | "stepAfter" | "stepBefore"
      //enablePointLabel
      enableTouchCrosshair
      enableSlices="x"
      margin={{
        bottom: 30,
        left: 50,
        right: 5,
        top: 30,
      }}
      sliceTooltip={(p) => {
        return customSliceTooltip(p, checkPoint);
      }}
      tooltip={customTooltip}
      enablePointLabel
      pointLabelYOffset={24}
      pointBorderColor={{
        from: "color",
        modifiers: [["darker", 0.3]],
      }}
      pointBorderWidth={1}
      pointSize={8}
      pointSymbol={(p) => {
        //Debug(p);
        if (p.datum.x) {
          const check = new Date(p.datum.x).getTime();
          return checkArr.includes(check) ? (
            <circle
              r={p.size / 2}
              fill={p.color}
              stroke={"rgb(208, 173, 144)"}
              strokeWidth={p.borderWidth}
              style={{ pointerEvents: "none" }}
            ></circle>
          ) : undefined;
        }
      }}
      pointLabel={(d) => {
        return d.data.x && d.seriesId == "bean" && checkArr.includes(new Date(d.data.x).getTime()) ? d.data.yFormatted + "" : "";
      }}
      useMesh
      xFormat="time:%M:%S"
      xScale={{
        format: "%M:%S",
        precision: "second",
        type: "time",
        useUTC: false,
        min: new Date(0),
        max: new Date(780000),
      }}
      yFormat={(a) => {
        return a + "°C";
      }}
      yScale={{
        type: "linear",
        min: 100,
        max: 250,
      }}
      enableArea
      areaOpacity={0.3}
      areaBaselineValue={100}
      defs={[
        {
          colors: [
            {
              color: "inherit",
              offset: 0,
              opacity: 0.2,
            },
            {
              color: "inherit",
              offset: 45,
              opacity: 0,
            },
          ],
          id: "clear",
          type: "linearGradient",
        },
        {
          colors: [
            {
              color: "inherit",
              offset: 0,
              opacity: 0.7,
            },
            {
              color: "inherit",
              offset: 70,
              opacity: 0,
            },
          ],
          id: "fill",
          type: "linearGradient",
        },
      ]}
      fill={[
        {
          id: "clear",
          match: (d) => d.id != "bean",
        },
        {
          id: "fill",
          match: (d) => d.id == "bean",
        },
      ]}
      markers={checkArr.map((check) => {
        const result: CartesianMarkerProps<DatumValue> = {
          axis: "x",
          //legend: checkPoint[check],
          lineStyle: lineStyle,
          value: check,
        };
        return result;
      })}
    />
  );
}
const getRadarInputType = (params: DataInputParams): InputTypeData => {
  switch (params.inputType) {
    case "radarRate":
      return "rateInput";
    case "radarSlider":
      return "sliderInput";
    case "radarInput":
    default:
      return "inputNumber";
  }
};
export default function DataTimeLine(params: DataInputParams) {
  const t = useTranslations(params.transitionGroup ?? "contents");
  const to = useTranslations();
  const prev = params.prev ? GetDataInputElement(params.prev, false, params.values, t) : undefined;
  const next = params.next ? GetDataInputElement(params.next, true, params.values, t) : undefined;
  const dataLabel: string | DataElement = params.label ?? (params.labelNamePrev ?? "") + params.name;

  const DataLabel: DataElement =
    typeof params.label === "string" || params.label === undefined
      ? { element: params.label ?? (params.labelNamePrev ?? "") + params.name }
      : params.label;

  const indexInfo = useMemo(() => {
    const name = getDataKeyName(params.indexKey);
    const keys = params.radarProp?.keys ?? getDataKeyList(params) ?? [];
    const dataLabel = getDataKeyLabel(params);
    const radarKeys = params.radarProp?.keys ?? dataLabel;
    const keyLabel = radarKeys ? getStringRecord(radarKeys) : undefined;
    const keysArr = keys.map((key) => {
      return keyLabel ? keyLabel[key] : key;
    });
    Debug(params.inputMode, params.values, keys, keyLabel, dataLabel, keysArr);
    const fields = params.fieldList ? params.fieldList.map((item) => getStringArr(item)) : undefined;
    const fieldLabel = params.fieldList ? params.fieldList.map((item) => getStringRecord(item)) : [{}];
    const multi = keys && keys.length > 1;
    const convert = params.radarProp?.data === undefined || (params.radarProp?.dataConvert ?? true);
    const indexBy = params.radarProp?.indexBy ?? "field";
    return { name, keys, keyLabel, keysArr, fields, fieldLabel, indexBy, multi, convert };
  }, [params]);
  //Debug(indexInfo);
  const [data, setData] = useState<Record<string, unknown>[]>(dataConverter(t, indexInfo, params));
  if (params.resetFunction && params.name) {
    params.resetFunction[params.name] = () => {
      setData(dataConverter(t, indexInfo, params));
    };
  }
  useEffect(() => {
    const changeData = dataConverter(t, indexInfo, params);
    setData(changeData);
  }, [indexInfo, params, t]);

  const radarProp: RadarProps = {
    maxValue: typeof params.maxValue === "number" ? params.maxValue : undefined,
    keys: indexInfo.keys,
    indexBy: indexInfo.indexBy,
    ...params.radarProp,
  };
  if (params.asChild ?? false) {
    return (
      <>
        {(params.labelUse ?? true) && (DataLabel.display ?? true) ? <GetDataLabel {...params} /> : ""}
        <Flex className={cn("data-chart overflow-hidden", params.radarProp?.className ?? "flex-col h-[30rem] w-full")}>
          <DataRadarItem {...radarProp} data={data} />
        </Flex>
      </>
    );
  } else {
    const inputChild = params.radarProp?.commonInputProp?.asChild ?? false;
    const useLabel = (params.labelUse ?? true) && (DataLabel.display ?? true);
    const mergeValueStyle = useLabel ? params.valueStyle : { ...params.valueStyle, gridColumn: "1 / span 2" };

    return (
      <>
        <DataList.Item
          className={cn(params.itemClassName, params.nextItemHidden ? "next-item-hidden" : "", (params.show ?? true) ? "" : "hidden")}
        >
          {useLabel ? (
            <DataList.Label className={params.labelClassName} style={params.labelStyle}>
              <GetDataLabel {...params} />
            </DataList.Label>
          ) : (
            ""
          )}
          <DataList.Value className={params.valueClassName} style={mergeValueStyle}>
            <Skeleton loading={params.loading ?? true}>
              {/* <Container className={params.radarProp?.className ?? "flex-col"}> */}
              {prev}
              <Flex className={cn("data-chart overflow-hidden", params.radarProp?.className ?? "flex-col h-[30rem] w-full")}>
                <DataRadarItem {...radarProp} data={data} keys={indexInfo.keysArr} />
              </Flex>
              {next}
              {/* </Container> */}
            </Skeleton>
          </DataList.Value>
        </DataList.Item>
      </>
    );
  }
}
